import React, { useEffect, useState } from 'react';
import { Card, CardContent, Typography, Box, Stack, Grid, CircularProgress } from '@mui/material';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { planoAcaoService } from '../../services/PlanoAcaoService';
import AcaoPorArea from '../../types/negocio/AcaoPorArea';

interface AcoesPorAreaChartProps {
	data?: AcaoPorArea[];
	loading?: boolean;
}

// Cores para cada área no gráfico de pizza
const COLORS = ['#4e79a7', '#f28e2c', '#aaa', '#fdc36e', '#59a14f', '#e15759', '#76b7b2', '#edc949'];

const AcoesPorAreaChart: React.FC<AcoesPorAreaChartProps> = ({
	loading: initialLoading = false
}) => {
	const [data, setData] = useState<AcaoPorArea[]>([]);
	const [loading, setLoading] = useState(initialLoading);

	useEffect(() => {
		const fetchData = async () => {
			try {
				setLoading(true);
				const result = await planoAcaoService.buscarAcoesPorArea();
				setData(result);
			} catch (error) {
				console.error('Erro ao buscar dados de ações por área:', error);
				setData([]);
			} finally {
				setLoading(false);
			}
		};

		fetchData();
	}, []);

	if (loading) {
		return (
			<Card elevation={0}>
				<CardContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
					<CircularProgress />
				</CardContent>
			</Card>
		);
	}

	if (data.length === 0) {
		return (
			<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '220px' }}>
				<Typography variant="body1" color="text.secondary">
					Nenhum dado disponível
				</Typography>
			</Box>
		);
	}

	return (


		<Stack
			direction={{ xs: 'column', md: 'row' }}
			spacing={2}
		>
			{/* Grid de dados (substituindo a tabela) */}
			<Box sx={{
				border: '1px solid #e0e0e0',
				borderRadius: 1,
				minHeight: '220px',
				flex: { xs: '1', md: '0 0 30%' },
				padding: '8px'
			}}>
				<Grid container>
					{/* Cabeçalho */}
					<Grid size={{ xs: 6 }} sx={{
						fontWeight: 'bold',
						borderBottom: '1px solid #e0e0e0',
						paddingBottom: '8px'
					}}>
						Área
					</Grid>
					<Grid size={{ xs: 6 }} sx={{
						fontWeight: 'bold',
						textAlign: 'right',
						borderBottom: '1px solid #e0e0e0',
						paddingBottom: '8px'
					}}>
						Nro ações
					</Grid>

					{/* Linhas de dados */}
					{data.map((item, index) => (
						<React.Fragment key={index}>
							<Grid size={{ xs: 6 }} sx={{
								padding: '8px 0',
								borderBottom: index < data.length - 1 ? '1px solid #f5f5f5' : 'none'
							}}>
								{item.area}
							</Grid>
							<Grid size={{ xs: 6 }} sx={{
								padding: '8px 0',
								textAlign: 'right',
								borderBottom: index < data.length - 1 ? '1px solid #f5f5f5' : 'none'
							}}>
								{item.quantidade}
							</Grid>
						</React.Fragment>
					))}
				</Grid>
			</Box>

			{/* Gráfico de pizza */}
			<Box sx={{
				height: 250,
				flex: { xs: '1', md: '0 0 70%' }
			}}>
				<ResponsiveContainer width="100%" height="100%">
					<PieChart>
						<Pie
							data={data}
							cx="50%"
							cy="50%"
							labelLine={false}
							outerRadius={80}
							fill="#8884d8"
							dataKey="quantidade"
							nameKey="area"
							label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
						>
							{data.map((entry, index) => (
								<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
							))}
						</Pie>
						<Tooltip
							formatter={(value, name) => [`${value} ações`, name]}
							labelFormatter={() => ''}
						/>
						<Legend />
					</PieChart>
				</ResponsiveContainer>
			</Box>
		</Stack>
	);
};

export default AcoesPorAreaChart;
